import React, { useState, useEffect } from 'react';
import {
	Box,
	Button,
	ButtonGroup,
	Heading,
	Input,
	InputGroup,
	InputLeftElement,
	InputRightElement,
	IconButton,
	Table,
	Thead,
	Tbody,
	Tr,
	Th,
	Td,
	Badge,
	Modal,
	ModalOverlay,
	ModalContent,
	ModalHeader,
	ModalBody,
	ModalFooter,
	ModalCloseButton,
	FormControl,
	FormLabel,
	Select,
	Spinner,
	useToast,
	HStack,
	Stack,
	SimpleGrid,
	Flex,
	Text,
} from '@chakra-ui/react';
import { EditIcon, DeleteIcon, AddIcon, SearchIcon, LockIcon, ViewIcon, ViewOffIcon } from '@chakra-ui/icons';
import { connect } from 'react-redux';
import { getAllUsers, createUser, updateUser, resetUserPassword } from '../../redux/actions/userActions';
import { getAllFacilityList } from '../../redux/actions/facilityActions';
import {
	useReactTable,
	createColumnHelper,
	flexRender,
	getCoreRowModel,
} from '@tanstack/react-table';
import AdminNavbar from '../../component/adminNavbar';
import Alert from '../../component/alert';

const Users = ({
	getAllUsers,
	loading,
	users,
	createUser,
	updateUser,
	resetUserPassword,
	count,
	getAllFacilityList,
	facilities,
}) => {
	const [offset, setOffset] = useState(0);
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [nameFilter, setNameFilter] = useState('');
	const [emailFilter, setEmailFilter] = useState('');
	const [roleFilter, setRoleFilter] = useState('');
	const [statusFilter, setStatusFilter] = useState('');
	const [facilityFilter, setFacilityFilter] = useState('');
	const [isSearching, setIsSearching] = useState(false);
	const [selectedUser, setSelectedUser] = useState(null);
	const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
	const [userToDelete, setUserToDelete] = useState(null);
	const [isResetModalOpen, setIsResetModalOpen] = useState(false);
	const [userToReset, setUserToReset] = useState(null);
	const [newPassword, setNewPassword] = useState('');
	const [confirmPassword, setConfirmPassword] = useState('');
	const [showCreatePw, setShowCreatePw] = useState(false);
	const [showResetNew, setShowResetNew] = useState(false);
	const [showResetConfirm, setShowResetConfirm] = useState(false);
	const [formState, setFormState] = useState({
		id: '',
		name: '',
		email: '',
		fac_name: '',
		fac_id: '',
		password: '',
		is_admin: '0',
	});

	const toast = useToast();
	const columnHelper = createColumnHelper();

	useEffect(() => {
		getAllFacilityList(null)
	}, [getAllFacilityList]);

	useEffect(() => {
		getAllUsers(
			offset,
			nameFilter,
			emailFilter,
			roleFilter,
			statusFilter,
			facilityFilter
		).then(() => {
			if (isSearching) {
				setIsSearching(false);
			}
		});
	}, [offset, isSearching]);

	const handleSearchClick = () => {
		setNameFilter((v) => v.trim());
		setEmailFilter((v) => v.trim());
		setOffset(0);
		setIsSearching(true);
	};

	const handleClearClick = () => {
		setNameFilter('');
		setEmailFilter('');
		setRoleFilter('');
		setStatusFilter('');
		setFacilityFilter('');
		setOffset(0);
		setIsSearching(true);
	};

	const columns = [
		columnHelper.accessor('name', {
			cell: (info) => info.getValue(),
			header: 'NAME',
		}),
		columnHelper.accessor('email', {
			cell: (info) => info.getValue(),
			header: 'EMAIL',
		}),
		columnHelper.accessor('is_admin', {
			cell: (info) => (
				<Badge colorScheme={info.getValue() == 1 ? 'green' : 'yellow'}>
					{info.getValue() == 1 ? 'ADMIN' : 'USER'}
				</Badge>
			),
			header: 'ROLE',
		}),
		columnHelper.accessor('status', {
			cell: (info) => (
				<Badge colorScheme={info.getValue() == 'A' ? 'green' : 'red'}>
					{info.getValue() == 'A' ? 'ACTIVE' : 'INACTIVE'}
				</Badge>
			),
			header: 'ROLE',
		}),
		columnHelper.accessor('fac_name', {
			cell: (info) => info.getValue(),
			header: 'FACILITY',
		}),
		columnHelper.accessor('action', {
			cell: ({ row }) => (
				<HStack spacing={2}>
					<ButtonGroup size="xs">
						<Button
							colorScheme="blue"
							onClick={() => handleOpenModal(row.original)}
						>
							<EditIcon />
						</Button>
						<Button
							colorScheme="yellow"
							onClick={() => handleOpenResetModal(row.original)}
							title="Reset Password"
						>
							<LockIcon />
						</Button>
						<Button colorScheme="red" onClick={() => handleOpenDeleteModal(row.original)}>
							<DeleteIcon />
						</Button>
					</ButtonGroup>
				</HStack>
			),
			header: 'ACTION',
		}),
	];

	const table = useReactTable({
		columns,
		data: users || [],
		getCoreRowModel: getCoreRowModel(),
	});

	const handleOpenModal = (user = null) => {
		setSelectedUser(user);
		if (user) {
			setFormState({
				...user,
				fac_id: user.facilityId, // Map facilityId to fac_id
			});
		} else {
			setFormState({
				id: '',
				name: '',
				email: '',
				fac_name: '',
				password: '',
				is_admin: '0',
				fac_id: '',
			});
		}
		setIsModalOpen(true);
	};

	const handleCloseModal = () => {
		setIsModalOpen(false);
		setSelectedUser(null);
	};

	const handleOpenDeleteModal = (user) => {
		setUserToDelete(user);
		setIsDeleteModalOpen(true);
	};

	const handleCloseDeleteModal = () => {
		setUserToDelete(null);
		setIsDeleteModalOpen(false);
	};

	const handleOpenResetModal = (user) => {
		setUserToReset(user);
		setNewPassword('');
		setConfirmPassword('');
		setIsResetModalOpen(true);
	};

	const handleCloseResetModal = () => {
		setUserToReset(null);
		setNewPassword('');
		setConfirmPassword('');
		setShowResetNew(false);
		setShowResetConfirm(false);
		setIsResetModalOpen(false);
	};

	const handleResetPassword = async () => {
		if (newPassword.length < 6) {
			toast({ title: 'Password must be at least 6 characters.', status: 'warning', duration: 3000, isClosable: true });
			return;
		}
		if (newPassword !== confirmPassword) {
			toast({ title: 'Passwords do not match.', status: 'warning', duration: 3000, isClosable: true });
			return;
		}
		try {
			await resetUserPassword(userToReset.user_id, newPassword);
			handleCloseResetModal();
		} catch (error) {
			// alert already dispatched by action
		}
	};

	const confirmDelete = async () => {
		if (userToDelete) {
			const token = localStorage.getItem('x-auth-token');
			if (token) {
				try {
					const payload = JSON.parse(atob(token.split('.')[1]));
					if (payload.user_id === userToDelete.user_id) {
						toast({ title: 'You cannot delete your own account.', status: 'error', duration: 3000, isClosable: true });
						handleCloseDeleteModal();
						return;
					}
				} catch {}
			}
			try {
				await updateUser(userToDelete.user_id, { ...userToDelete, fac_id: userToDelete.facilityId, status: 'D' });
				toast({
					title: 'User deleted.',
					status: 'success',
					duration: 3000,
					isClosable: true,
				});
				getAllUsers(offset, nameFilter, emailFilter, roleFilter, statusFilter, facilityFilter);
				handleCloseDeleteModal();
			} catch (error) {
				toast({ title: 'Error deleting user.', status: 'error', duration: 3000, isClosable: true });
			}
		}
	};

	const handleChange = (e) => {
		const { name, value } = e.target;
		setFormState((prev) => ({
			...prev,
			[name]: value,
			...(name === 'is_admin' && value === '1' ? { fac_id: '' } : {}),
		}));
	};

	const handleSave = async () => {
		const userData = { ...formState };
		if (selectedUser) {
			delete userData.password;
		}

		if (userData.is_admin == '0' && !userData.fac_id) {
			toast({
				title: 'Facility is required for non-admin users.',
				status: 'warning',
				duration: 3000,
				isClosable: true,
			});
			return;
		}

		try {
			if (selectedUser) {
				await updateUser(selectedUser.user_id, userData);
				toast({
					title: 'User updated.',
					status: 'success',
					duration: 3000,
					isClosable: true,
				});
			} else {
				await createUser(userData);
				toast({
					title: 'User created.',
					status: 'success',
					duration: 3000,
					isClosable: true,
				});
			}
			getAllUsers(offset, nameFilter, emailFilter, roleFilter, statusFilter, facilityFilter);
			handleCloseModal();
		} catch (error) {
			toast({
				title: `Error ${selectedUser ? 'updating' : 'creating'} user.`,
				status: 'error',
				duration: 3000,
				isClosable: true,
			});
		}
	};

	const Tabs = ({ count, onOffsetChange, offset }) => {
		const groupSize = 25;
		const numPages = Math.ceil(count / groupSize);
		const currentPage = Math.floor(offset / groupSize);

		if (numPages <= 1) return null;

		const pages: (number | 'ellipsis')[] = [0];
		const rangeStart = Math.max(1, currentPage - 1);
		const rangeEnd = Math.min(numPages - 2, currentPage + 1);
		if (rangeStart > 1) pages.push('ellipsis');
		for (let i = rangeStart; i <= rangeEnd; i++) pages.push(i);
		if (rangeEnd < numPages - 2) pages.push('ellipsis');
		if (numPages > 1) pages.push(numPages - 1);

		return (
			<Flex mt={6} justify="center" align="center" gap={1}>
				<Button size="sm" variant="outline" onClick={() => onOffsetChange(Math.max(0, offset - groupSize))} isDisabled={currentPage === 0}>‹</Button>
				{pages.map((page, idx) =>
					page === 'ellipsis' ? (
						<Text key={`e-${idx}`} px={2} color="gray.400">…</Text>
					) : (
						<Button key={page} size="sm" colorScheme={currentPage === page ? 'blue' : 'gray'} variant={currentPage === page ? 'solid' : 'outline'} onClick={() => onOffsetChange((page as number) * groupSize)}>
							{(page as number) + 1}
						</Button>
					)
				)}
				<Button size="sm" variant="outline" onClick={() => onOffsetChange(Math.min((numPages - 1) * groupSize, offset + groupSize))} isDisabled={currentPage === numPages - 1}>›</Button>
			</Flex>
		);
	};

	return (
		<>
			<AdminNavbar />
			<Alert />
			{!loading && (
				<Box pt={{ base: 16, md: 6 }} px={{ base: 4, md: 6 }} pb={6} ml={{ base: 0, md: "250px" }} bg="gray.50" minH="100vh">
					<Heading mb={6} fontSize="2xl" fontWeight="bold" color="gray.700">
						Users
					</Heading>
					<Box bg="white" p={4} borderRadius="lg" boxShadow="sm" mb={6}>
						<SimpleGrid columns={{ base: 2, md: 3, lg: 5 }} spacing={3} mb={3}>
							<Input
								placeholder="Name"
								size="sm"
								borderRadius="md"
								value={nameFilter}
								onChange={(e) => setNameFilter(e.target.value)}
								isDisabled={loading}
							/>
							<Input
								placeholder="Email"
								size="sm"
								borderRadius="md"
								value={emailFilter}
								onChange={(e) => setEmailFilter(e.target.value)}
								isDisabled={loading}
							/>
							<Select
								placeholder="All Roles"
								size="sm"
								borderRadius="md"
								value={roleFilter}
								onChange={(e) => setRoleFilter(e.target.value)}
								isDisabled={loading}
							>
								<option value="1">Admin</option>
								<option value="0">User</option>
							</Select>
							<Select
								placeholder="All Statuses"
								size="sm"
								borderRadius="md"
								value={statusFilter}
								onChange={(e) => setStatusFilter(e.target.value)}
								isDisabled={loading}
							>
								<option value="A">Active</option>
								<option value="I">Inactive</option>
							</Select>
							<Select
								placeholder="All Facilities"
								size="sm"
								borderRadius="md"
								value={facilityFilter}
								onChange={(e) => setFacilityFilter(e.target.value)}
								isDisabled={loading || !facilities}
							>
								{facilities?.map((fac) => (
									<option key={fac.facilityId} value={fac.facilityId}>
										{fac.name}
									</option>
								))}
							</Select>
						</SimpleGrid>
						<Flex justify="space-between" flexWrap="wrap" gap={2}>
							<Button
								size="sm"
								colorScheme="green"
								leftIcon={<AddIcon />}
								onClick={() => handleOpenModal()}
								variant="outline"
							>
								Create User
							</Button>
							<HStack spacing={2}>
								<Button
									size="sm"
									variant="ghost"
									colorScheme="gray"
									onClick={handleClearClick}
									isDisabled={loading}
								>
									Clear Filters
								</Button>
								<Button
									size="sm"
									colorScheme="blue"
									onClick={handleSearchClick}
									isLoading={isSearching}
									leftIcon={<SearchIcon />}
								>
									Search
								</Button>
							</HStack>
						</Flex>
					</Box>

					<Box overflowX="auto" borderRadius="lg" bg="white" boxShadow="md">
						<Table variant="simple" colorScheme="gray" size="sm">
							<Thead>
								{table.getHeaderGroups().map((headerGroup) => (
									<Tr key={headerGroup.id}>
										{headerGroup.headers.map((header) => (
											<Th key={header.id}>
												{flexRender(
													header.column.columnDef.header,
													header.getContext()
												)}
											</Th>
										))}
									</Tr>
								))}
							</Thead>
							<Tbody>
								{table.getRowModel().rows.map((row) => (
									<Tr
										key={row.id}
										_hover={{ bg: 'blue.100' }}
										transition="background 0.2s ease"
									>
										{row.getVisibleCells().map((cell) => (
											<Td key={cell.id}>
												{flexRender(
													cell.column.columnDef.cell,
													cell.getContext()
												)}
											</Td>
										))}
									</Tr>
								))}
							</Tbody>
						</Table>
					</Box>

					<Tabs count={count} onOffsetChange={setOffset} offset={offset} />

					<Modal isOpen={isModalOpen} onClose={handleCloseModal} isCentered>
						<ModalOverlay />
						<ModalContent>
							<ModalHeader>
								{selectedUser ? 'Edit User' : 'Create User'}
							</ModalHeader>
							<ModalCloseButton />
							<ModalBody>
								<Stack spacing={4}>
									<FormControl>
										<FormLabel>Name</FormLabel>
										<Input
											name="name"
											value={formState.name}
											onChange={handleChange}
										/>
									</FormControl>
									<FormControl>
										<FormLabel>Email</FormLabel>
										<Input
											name="email"
											value={formState.email}
											onChange={handleChange}
										/>
									</FormControl>
									<FormControl>
										<FormLabel>Role</FormLabel>
										<Select
											name="is_admin"
											value={formState.is_admin}
											onChange={handleChange}
										>
											<option value="0">User</option>
											<option value="1">Admin</option>
										</Select>
									</FormControl>
									{formState.is_admin != '1' && (
									<FormControl isRequired>
										<FormLabel>Facility</FormLabel>
										<Select
											name="fac_id"
											value={formState.fac_id}
											onChange={handleChange}
											placeholder={formState.fac_name || 'Select Facility'}
										>
											{facilities &&
												facilities.map((fac) => (
													<option key={fac.facilityId} value={fac.facilityId}>
														{fac.name}
													</option>
												))}
										</Select>
									</FormControl>
								)}
									{!selectedUser && (
										<FormControl>
											<FormLabel>Password</FormLabel>
											<InputGroup>
												<Input
													name="password"
													type={showCreatePw ? 'text' : 'password'}
													value={formState.password}
													onChange={handleChange}
												/>
												<InputRightElement>
													<IconButton
														aria-label="Toggle password"
														icon={showCreatePw ? <ViewOffIcon /> : <ViewIcon />}
														size="xs"
														variant="ghost"
														onClick={() => setShowCreatePw((v) => !v)}
													/>
												</InputRightElement>
											</InputGroup>
										</FormControl>
									)}
								</Stack>
							</ModalBody>
							<ModalFooter>
								<Button colorScheme="blue" mr={3} onClick={handleSave}>
									Save
								</Button>
								<Button variant="ghost" onClick={handleCloseModal}>
									Close
								</Button>
							</ModalFooter>
						</ModalContent>
					</Modal>

					<Modal isOpen={isResetModalOpen} onClose={handleCloseResetModal} isCentered>
						<ModalOverlay />
						<ModalContent>
							<ModalHeader>Reset Password — {userToReset?.name}</ModalHeader>
							<ModalCloseButton />
							<ModalBody>
								<Stack spacing={4}>
									<FormControl isRequired>
										<FormLabel>New Password</FormLabel>
										<InputGroup>
											<Input
												type={showResetNew ? 'text' : 'password'}
												value={newPassword}
												onChange={(e) => setNewPassword(e.target.value)}
												placeholder="Min. 6 characters"
											/>
											<InputRightElement>
												<IconButton
													aria-label="Toggle password"
													icon={showResetNew ? <ViewOffIcon /> : <ViewIcon />}
													size="xs"
													variant="ghost"
													onClick={() => setShowResetNew((v) => !v)}
												/>
											</InputRightElement>
										</InputGroup>
									</FormControl>
									<FormControl isRequired>
										<FormLabel>Confirm Password</FormLabel>
										<InputGroup>
											<Input
												type={showResetConfirm ? 'text' : 'password'}
												value={confirmPassword}
												onChange={(e) => setConfirmPassword(e.target.value)}
											/>
											<InputRightElement>
												<IconButton
													aria-label="Toggle password"
													icon={showResetConfirm ? <ViewOffIcon /> : <ViewIcon />}
													size="xs"
													variant="ghost"
													onClick={() => setShowResetConfirm((v) => !v)}
												/>
											</InputRightElement>
										</InputGroup>
									</FormControl>
								</Stack>
							</ModalBody>
							<ModalFooter>
								<Button colorScheme="blue" mr={3} onClick={handleResetPassword}>
									Reset Password
								</Button>
								<Button variant="ghost" onClick={handleCloseResetModal}>
									Cancel
								</Button>
							</ModalFooter>
						</ModalContent>
					</Modal>

					<Modal isOpen={isDeleteModalOpen} onClose={handleCloseDeleteModal} isCentered>
						<ModalOverlay />
						<ModalContent>
							<ModalHeader>Delete User</ModalHeader>
							<ModalCloseButton />
							<ModalBody>
								Are you sure you want to delete {userToDelete?.name}?
							</ModalBody>
							<ModalFooter>
								<Button mr={3} onClick={handleCloseDeleteModal}>Cancel</Button>
								<Button colorScheme="red" onClick={confirmDelete}>Delete</Button>
							</ModalFooter>
						</ModalContent>
					</Modal>
				</Box>
			)}
		</>
	);
};

const mapStateToProps = (state) => ({
	loading: state.user.loading,
	users: state.user.users,
	count: state.user.count,
	facloading: state.facilities.facListLoading,
	facilities: state.facilities.facList,
});

const mapDispatchToProps = {
	getAllUsers:(offset, name, email, role, status, facility) =>
	getAllUsers(offset, name, email, role, status, facility),
	createUser,
	updateUser,
	resetUserPassword,
	getAllFacilityList
};

export default connect(mapStateToProps, mapDispatchToProps)(Users);
